import { supabase } from '../supabase/client';

export interface PDFUploadResult {
  success: boolean;
  url?: string;
  path?: string;
  error?: string;
}

export interface UserBucketInfo {
  bucketName: string;
  exists: boolean;
  error?: string;
}

/**
 * Creates a private storage bucket for a user if it doesn't exist
 * Updated to use individual user buckets (user-{userId})
 */
export const ensureUserBucket = async (userId: string): Promise<UserBucketInfo> => {
  // Individual user bucket - created automatically by database trigger
  const bucketName = `user-${userId}`;

  // The bucket should exist due to the create_user_bucket trigger
  // If it doesn't exist, the upload will fail with a clear error message
  return { bucketName, exists: true };
};

/**
 * Uploads a PDF blob to the user's private bucket
 * Enhanced with better error handling and fallbacks
 */
export const uploadPDFToUserBucket = async (
  userId: string,
  pdfBlob: Blob,
  filename: string,
  metadata?: Record<string, any>
): Promise<PDFUploadResult> => {
  try {
    // Ensure user bucket exists
    const bucketInfo = await ensureUserBucket(userId);
    if (!bucketInfo.exists) {
      return { 
        success: false, 
        error: `Bucket access failed: ${bucketInfo.error}. Cloud storage is temporarily unavailable.` 
      };
    }
    
    // Create a unique filename with timestamp
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const uniqueFilename = `${timestamp}_${filename}`;
    const filePath = `pdfs/${uniqueFilename}`;
    
    // Upload the file
    const { data, error } = await supabase.storage
      .from(bucketInfo.bucketName)
      .upload(filePath, pdfBlob, {
        contentType: 'application/pdf',
        metadata: {
          ...metadata,
          uploadedAt: new Date().toISOString(),
          originalFilename: filename
        }
      });
    
    if (error) {
      console.error('Error uploading PDF:', error);
      
      // Provide user-friendly error messages
      if (error.message.includes('row-level security') || 
          error.message.includes('policy') || 
          error.message.includes('permission')) {
        return { 
          success: false, 
          error: 'Storage permission error. Please try signing out and back in, or contact support.' 
        };
      }
      
      if (error.message.includes('size') || error.message.includes('too large')) {
        return { 
          success: false, 
          error: 'File too large. PDF must be under 10MB.' 
        };
      }
      
      return { success: false, error: `Upload failed: ${error.message}` };
    }
    
    // Get signed URL for accessing the file
    const { data: signedUrlData, error: urlError } = await supabase.storage
      .from(bucketInfo.bucketName)
      .createSignedUrl(filePath, 3600); // 1 hour expiry
    
    if (urlError) {
      console.warn('File uploaded but could not create signed URL:', urlError);
      return { 
        success: true, 
        path: data.path,
        error: `File uploaded but URL generation failed: ${urlError.message}` 
      };
    }
    
    console.log(`✅ PDF uploaded successfully: ${filePath}`);
    
    return {
      success: true,
      url: signedUrlData.signedUrl,
      path: data.path
    };
    
  } catch (error: any) {
    console.error('Unexpected error uploading PDF:', error);
    
    // Provide helpful error messages
    if (error.message.includes('network') || error.message.includes('fetch')) {
      return { 
        success: false, 
        error: 'Network error. Please check your internet connection and try again.' 
      };
    }
    
    return { success: false, error: `Upload failed: ${error.message}` };
  }
};

/**
 * Uploads a permit-package file (PDF, IFC, CAD, etc.) to the user's private bucket.
 * Isolated helper for /permitreview — does not change the PDF report upload path.
 */
export const uploadPermitFileToUserBucket = async (
  userId: string,
  file: File | Blob,
  filename: string,
  contentType?: string,
  metadata?: Record<string, any>
): Promise<PDFUploadResult> => {
  try {
    const bucketInfo = await ensureUserBucket(userId);
    if (!bucketInfo.exists) {
      return {
        success: false,
        error: `Bucket access failed: ${bucketInfo.error}. Cloud storage is temporarily unavailable.`,
      };
    }

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const safeName = filename.replace(/[^a-zA-Z0-9._-]/g, '_');
    const uniqueFilename = `${timestamp}_${safeName}`;
    const filePath = `permits/${uniqueFilename}`;
    const resolvedType =
      contentType ||
      (file instanceof File ? file.type : undefined) ||
      'application/octet-stream';

    const { data, error } = await supabase.storage
      .from(bucketInfo.bucketName)
      .upload(filePath, file, {
        contentType: resolvedType,
        metadata: {
          ...metadata,
          uploadedAt: new Date().toISOString(),
          originalFilename: filename,
        },
      });

    if (error) {
      console.error('Error uploading permit file:', error);
      return { success: false, error: `Upload failed: ${error.message}` };
    }

    const { data: signedUrlData, error: urlError } = await supabase.storage
      .from(bucketInfo.bucketName)
      .createSignedUrl(filePath, 3600);

    if (urlError) {
      return {
        success: true,
        path: data.path,
        error: `File uploaded but URL generation failed: ${urlError.message}`,
      };
    }

    return {
      success: true,
      url: signedUrlData.signedUrl,
      path: data.path,
    };
  } catch (error: any) {
    console.error('Unexpected error uploading permit file:', error);
    return { success: false, error: `Upload failed: ${error.message}` };
  }
};

/**
 * Lists all PDFs in the user's bucket
 */
export const listUserPDFs = async (userId: string): Promise<{
  success: boolean;
  files?: any[];
  error?: string;
}> => {
  try {
    const bucketInfo = await ensureUserBucket(userId);
    if (!bucketInfo.exists) {
      return { success: false, error: 'User bucket not accessible' };
    }
    
    const { data, error } = await supabase.storage
      .from(bucketInfo.bucketName)
      .list('pdfs', {
        limit: 100,
        offset: 0,
        sortBy: { column: 'created_at', order: 'desc' }
      });
    
    if (error) {
      console.error('Error listing PDFs:', error);
      return { success: false, error: error.message };
    }
    
    return { success: true, files: data || [] };
    
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

/**
 * Deletes a PDF from the user's bucket
 */
export const deleteUserPDF = async (userId: string, filePath: string): Promise<{
  success: boolean;
  error?: string;
}> => {
  try {
    const bucketInfo = await ensureUserBucket(userId);
    if (!bucketInfo.exists) {
      return { success: false, error: 'User bucket not accessible' };
    }
    
    const { error } = await supabase.storage
      .from(bucketInfo.bucketName)
      .remove([filePath]);
    
    if (error) {
      return { success: false, error: error.message };
    }
    
    console.log(`✅ PDF deleted successfully: ${filePath}`);
    return { success: true };
    
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

/**
 * Gets a signed URL for accessing a PDF
 */
export const getPDFSignedUrl = async (
  userId: string, 
  filePath: string, 
  expiresIn: number = 3600
): Promise<PDFUploadResult> => {
  try {
    const bucketInfo = await ensureUserBucket(userId);
    if (!bucketInfo.exists) {
      return { success: false, error: 'User bucket not accessible' };
    }
    
    const { data, error } = await supabase.storage
      .from(bucketInfo.bucketName)
      .createSignedUrl(filePath, expiresIn);
    
    if (error) {
      return { success: false, error: error.message };
    }
    
    return {
      success: true,
      url: data.signedUrl,
      path: filePath
    };
    
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

/**
 * Helper to convert jsPDF document to blob
 */
export const convertJsPDFToBlob = (doc: any): Blob => {
  const pdfData = doc.output('blob');
  return new Blob([pdfData], { type: 'application/pdf' });
};

/**
 * Save report metadata to database with PDF storage reference
 * Enhanced with better error handling
 */
export const saveReportWithPDF = async (
  userId: string,
  reportData: {
    title: string;
    content: string;
    address: string;
    zone_code: string;
  },
  pdfBlob: Blob,
  filename: string
): Promise<{
  success: boolean;
  reportId?: string;
  pdfUrl?: string;
  error?: string;
}> => {
  try {
    // Upload PDF first
    const uploadResult = await uploadPDFToUserBucket(userId, pdfBlob, filename, {
      reportTitle: reportData.title,
      address: reportData.address,
      zoneCode: reportData.zone_code
    });
    
    if (!uploadResult.success) {
      return { 
        success: false, 
        error: `Cloud storage failed: ${uploadResult.error}` 
      };
    }
    
    // Save report metadata to database
    const { data, error } = await supabase
      .from('reports')
      .insert([{
        ...reportData,
        user_id: userId,
        pdf_path: uploadResult.path,
        pdf_url: uploadResult.url,
        created_at: new Date().toISOString()
      }])
      .select()
      .single();
    
    if (error) {
      console.error('Database save error:', error);
      
      // If database save fails, try to clean up the uploaded PDF
      if (uploadResult.path) {
        await deleteUserPDF(userId, uploadResult.path);
      }
      
      // Provide user-friendly error messages
      if (error.message.includes('reports') && error.message.includes('does not exist')) {
        return { 
          success: false, 
          error: 'Database not properly configured. Please contact support to enable saved reports.' 
        };
      }
      
      return { success: false, error: `Database error: ${error.message}` };
    }
    
    console.log('✅ Report saved with PDF storage successfully');
    
    return {
      success: true,
      reportId: data.id,
      pdfUrl: uploadResult.url
    };
    
  } catch (error: any) {
    console.error('Unexpected error saving report:', error);
    return { success: false, error: `Save failed: ${error.message}` };
  }
}; 

/**
 * Layout State Persistence for Mobile Auth Stability
 * Prevents the 'scroll up' appearance after authentication
 */

interface LayoutState {
  scrollY: number;
  timestamp: number;
  userAgent: string;
}

export function restoreLayoutState(): boolean {
  try {
    const storedState = sessionStorage.getItem('zoningpal_layout_state');
    
    if (!storedState) {
      return false;
    }
    
    const layoutState: LayoutState = JSON.parse(storedState);
    
    // Only restore if it's recent (within 30 seconds) and same device
    const isRecent = Date.now() - layoutState.timestamp < 30000;
    const isSameDevice = navigator.userAgent === layoutState.userAgent;
    
    if (!isRecent || !isSameDevice) {
      sessionStorage.removeItem('zoningpal_layout_state');
      return false;
    }
    
    // Restore scroll position after a brief delay to allow DOM to settle
    setTimeout(() => {
      window.scrollTo({
        top: layoutState.scrollY,
        behavior: 'auto' // Instant scroll to prevent visible jumping
      });
      
      console.log('📱 Restored layout state:', layoutState);
      
      // Clean up after restoration
      sessionStorage.removeItem('zoningpal_layout_state');
    }, 100);
    
    return true;
  } catch (error) {
    console.warn('Failed to restore layout state:', error);
    sessionStorage.removeItem('zoningpal_layout_state');
    return false;
  }
}

export function clearLayoutState(): void {
  sessionStorage.removeItem('zoningpal_layout_state');
} 